import { ChangeEvent, FC } from 'react';
import {
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { LuTrash2 } from 'react-icons/lu';
import type { AnimationEffect } from '@adflow/types';
import theme from '@adflow/theme';
import { NAMED_EASINGS, isNamedEasing, type NamedEasing } from '@adflow/utils';
import { getAnimationPreset, resolvePresetParams } from '../../../animation';

const EASING_LABELS: Record<NamedEasing, string> = {
  linear: 'Linear',
  'ease-in': 'Ease In',
  'ease-out': 'Ease Out',
  'ease-in-out': 'Ease In Out',
};

const fieldLabelStyles = {
  fontSize: 'xs',
  fontWeight: 'medium',
  color: 'gray.500',
  mb: 1,
};

type Props = {
  effect: AnimationEffect;
  onUpdate: (effectId: string, params: Record<string, number | string | boolean>) => void;
  onRemove: (effectId: string) => void;
};

export const AppliedEffectEditor: FC<Props> = ({ effect, onUpdate, onRemove }) => {
  const preset = getAnimationPreset(effect.presetId);
  const params = resolvePresetParams(preset, effect.params);
  const isSlide = preset.group === 'slide';

  const handleNumberParam =
    (key: 'duration' | 'start' | 'distance') => (_: string, value: number) => {
      if (!Number.isFinite(value)) {
        return;
      }
      onUpdate(effect.id, { [key]: value });
    };

  const handleEasingChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (isNamedEasing(event.target.value)) {
      onUpdate(effect.id, { easing: event.target.value });
    }
  };

  return (
    <Flex
      direction="column"
      gap={3}
      p={4}
      borderWidth="1px"
      borderColor={theme.palette.mcBlue.lighter}
      borderRadius="lg"
      bg="white"
      data-test-id={`applied-effect-${effect.id}`}
    >
      <Flex alignItems="center" justifyContent="space-between" gap={2}>
        <Flex alignItems="center" gap={2} minW={0}>
          <Icon
            as={HiOutlineSparkles}
            boxSize={4}
            color={theme.palette.mcNavy.main}
            flexShrink={0}
          />
          <Text fontSize="sm" fontWeight="bold" color={theme.palette.mcNavy.main} noOfLines={1}>
            {preset.label}
          </Text>
        </Flex>
        <IconButton
          aria-label={`Remove ${preset.label}`}
          icon={<LuTrash2 />}
          size="sm"
          variant="ghost"
          color="gray.600"
          onClick={() => onRemove(effect.id)}
          data-test-id={`applied-effect-remove-${effect.id}`}
        />
      </Flex>
      <SimpleGrid columns={2} spacing={3}>
        <FormControl>
          <FormLabel {...fieldLabelStyles}>Duration (s)</FormLabel>
          <NumberInput
            size="sm"
            min={0}
            step={0.1}
            precision={2}
            value={params.duration}
            onChange={handleNumberParam('duration')}
          >
            <NumberInputField data-test-id={`applied-effect-duration-${effect.id}`} />
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel {...fieldLabelStyles}>Start (s)</FormLabel>
          <NumberInput
            size="sm"
            min={0}
            step={0.1}
            precision={2}
            value={params.start}
            onChange={handleNumberParam('start')}
          >
            <NumberInputField data-test-id={`applied-effect-start-${effect.id}`} />
          </NumberInput>
        </FormControl>
      </SimpleGrid>
      <FormControl>
        <FormLabel {...fieldLabelStyles}>Easing</FormLabel>
        <Select
          size="sm"
          value={params.easing}
          onChange={handleEasingChange}
          data-test-id={`applied-effect-easing-${effect.id}`}
        >
          {(Object.keys(NAMED_EASINGS) as NamedEasing[]).map((easing) => (
            <option key={easing} value={easing}>
              {EASING_LABELS[easing]}
            </option>
          ))}
        </Select>
      </FormControl>
      {isSlide && (
        <FormControl>
          <FormLabel {...fieldLabelStyles}>Distance (px)</FormLabel>
          <NumberInput
            size="sm"
            min={0}
            step={1}
            precision={0}
            value={params.distance ?? 0}
            onChange={handleNumberParam('distance')}
          >
            <NumberInputField data-test-id={`applied-effect-distance-${effect.id}`} />
          </NumberInput>
        </FormControl>
      )}
    </Flex>
  );
};
